import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const externalOrderKeys = {
  all: ['externalOrders'],
  lists: () => [...externalOrderKeys.all, 'list'],
  list: (filters) => [...externalOrderKeys.lists(), filters || {}],
  details: () => [...externalOrderKeys.all, 'detail'],
  detail: (id) => [...externalOrderKeys.details(), id],
};

const buildListParams = (filtersOrStatus) => {
  const filters =
    typeof filtersOrStatus === 'string'
      ? { status: filtersOrStatus }
      : filtersOrStatus || {};
  const params = {};

  const allowedKeys = [
    'status',
    'search',
    'uuid_destination_warehouse',
    'uuid_project',
    'date_from',
    'date_to',
    'page',
    'per_page',
  ];

  allowedKeys.forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== '') {
      params[key] = value;
    }
  });

  return params;
};

export const useExternalOrders = (filtersOrStatus, options = {}) => {
  const params = buildListParams(filtersOrStatus);

  return useQuery({
    queryKey: externalOrderKeys.list(params),
    queryFn: async () => {
      const response = await apiClient.get('external_orders', { params });
      return response.data;
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useExternalOrder = (id, options = {}) => {
  return useQuery({
    queryKey: externalOrderKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`external_orders/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 30 * 1000,
    ...options,
  });
};

export const useCreateExternalOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderData) => {
      const response = await apiClient.post('external_orders', orderData);
      return response.data;
    },
    onSuccess: (createdOrder) => {
      queryClient.invalidateQueries({ queryKey: externalOrderKeys.lists() });
      if (createdOrder?.uuid) {
        queryClient.setQueryData(
          externalOrderKeys.detail(createdOrder.uuid),
          createdOrder
        );
      }
    },
    onError: (error) => {
      console.error('Error creating external order:', error);
    },
  });
};

export const useCheckExternalOrderStock = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.post(`external_orders/${id}/check-stock`);
      return response.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: externalOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: externalOrderKeys.detail(id) });
    },
    onError: (error) => {
      console.error('Error checking external order stock:', error);
    },
  });
};

export const useAuthorizeExternalOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.post(`external_orders/${id}/authorize`);
      return response.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: externalOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: externalOrderKeys.detail(id) });
    },
    onError: (error) => {
      console.error('Error authorizing external order:', error);
    },
  });
};

export const useSetExternalOrderConsumptionAuthorization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, consumption_authorized = true }) => {
      const response = await apiClient.put(
        `external_orders/${id}/consumption-authorization`,
        { consumption_authorized }
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: externalOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: externalOrderKeys.detail(variables.id) });
    },
    onError: (error) => {
      console.error('Error setting external order consumption authorization:', error);
    },
  });
};

export const useCancelExternalOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }) => {
      const response = await apiClient.post(`external_orders/${id}/cancel`, { reason });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: externalOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: externalOrderKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: ['supplyOrders'] });
      queryClient.invalidateQueries({ queryKey: ['stockTransfers'] });
      queryClient.invalidateQueries({ queryKey: ['consumptions'] });
    },
    onError: (error) => {
      console.error('Error cancelling external order:', error);
    },
  });
};

export const useProcessExternalOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.post(`external_orders/${id}/process`);
      return response.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: externalOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: externalOrderKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ['stockTransfers'] });
    },
    onError: (error) => {
      console.error('Error processing external order:', error);
    },
  });
};
